// components/tier-branch/TierUsersDialog.tsx
// Read-only, paginated list of the users currently in a tier.
import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
  CircularProgress, Table, TableHead, TableRow, TableCell, TableBody,
  Pagination, Typography,
} from "@mui/material";
import * as api from "../../services/tierBranchApi";
import type { Tier, TierBranchUser } from "../../types/tierBranch";
import type { Notify } from "./useSnack";
import { fmt } from "./useSnack";

const LIMIT = 25;

const TierUsersDialog: React.FC<{
  open: boolean;
  tier: Tier | null;
  onClose: () => void;
  notify: Notify;
}> = ({ open, tier, onClose, notify }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<TierBranchUser[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (!open || !tier) return;
    let alive = true;
    setLoading(true);
    api
      .getTierUsers(tier.id, { page, limit: LIMIT })
      .then((r) => {
        if (!alive) return;
        setRows(r.data);
        setTotalPages(r.totalPages);
      })
      .catch((e: any) => alive && notify(e?.message || "Failed to load users", "error"))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [open, tier, page, notify]);

  // Reset to page 1 whenever a different tier is opened.
  useEffect(() => {
    if (open) setPage(1);
  }, [open, tier]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{tier ? `Users in ${tier.name}` : "Users"}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Typography color="text.secondary">No users in this tier.</Typography>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell align="right">Txn Amount</TableCell>
                  <TableCell align="right">Txn Count</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.username}</TableCell>
                    <TableCell align="right">{fmt(u.totalTransactionAmount)}</TableCell>
                    <TableCell align="right">{u.transactionCount ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, p) => setPage(p)}
                  size="small"
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TierUsersDialog;
